import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";
import { calculateContainerAlgoPoints } from "@CommonUtils/pog/container";
import { planogramHoveredContainerAtom } from "src/components/PlanogramExtensions/store/atoms";
import { InnerWrapper, PLANOGRAM_LAYER_Z_INDEX } from "../../common/InnerWrapper";
import { DragBounds, DragModifications, Scaleable, Snappable } from "../../store/types";
import { PlanogramContainer } from "@CommonTypes/merchflow/pog/container";
import { MenuOption, useMenu } from "src/components/Menu";
import { KeyCode } from "src/utils/keys";
import { Container } from "./Container";
import { usePogAtomActions } from "../../store/actions";
import { createSplitOffsets } from "../KonvaPlanogram/sceneProjection";

interface Props extends Scaleable {
  bounds?: DragBounds;
  snappables: Snappable[];
  isEditable?: boolean;
  isContainersFullDetails: boolean;
  pogAtom: PrimitiveAtom<Pog>;
}

export const Containers = ({
  scaleX,
  scaleY,
  bounds,
  snappables,
  isEditable,
  isContainersFullDetails,
  pogAtom,
}: Props) => {
  const { openMenu } = useMenu();

  const [pog, setPog] = useAtom(pogAtom);
  const { unrangeContainer, deleteContainer, splitContainerBottom, splitContainerRight } =
    usePogAtomActions(pogAtom);
  const setHoveredContainer = useSetAtom(planogramHoveredContainerAtom);

  // Drawn and dragged where the bays are drawn, past any aisle-break gap; edits come back in pog x.
  const { toSceneX, toPogX } = createSplitOffsets(pog.planogram.bays);
  const rectToScene = <T extends DragBounds>(rect: T): T => {
    const xLeft = toSceneX(rect.xLeft);
    return { ...rect, xLeft, xRight: xLeft + rect.xRight - rect.xLeft };
  };
  const rectToPog = (rect: DragBounds): DragBounds => {
    const xLeft = toPogX(rect.xLeft);
    return { ...rect, xLeft, xRight: xLeft + rect.xRight - rect.xLeft };
  };

  const getSnappables = (containerToIgnore: PlanogramContainer) => {
    const containersSnappables = pog.containers.reduce((containers, container) => {
      return container.uniqueId !== containerToIgnore.uniqueId
        ? [
            ...containers,
            {
              xLeft: container.xLeft,
              xRight: container.xRight,
              yBottom: container.yBottom,
              yTop: container.yTop,
            },
          ]
        : containers;
    }, [] satisfies Snappable[]);

    return [...snappables, ...containersSnappables].map(rectToScene);
  };

  const onModify = (container: PlanogramContainer, sceneModifications: DragModifications) => {
    const modifications = rectToPog(sceneModifications);
    setPog((pog) => {
      const newPog = new Pog(pog.toNonCircular());
      const newContainer = newPog.containers.find((c) => c.uniqueId === container.uniqueId);
      if (newContainer) {
        newContainer.xLeft = modifications.xLeft;
        newContainer.xRight = modifications.xRight;
        newContainer.yBottom = modifications.yBottom;
        newContainer.yTop = modifications.yTop;

        // Container has algo target width requirements.
        if (newContainer.algoCurrentTargetWidth !== undefined) {
          newContainer.algoCurrentTargetWidth = calculateContainerAlgoPoints(
            newContainer,
            newPog,
          ).currentTargetWidth;
        }
      }

      return newPog;
    });
  };

  const onContainerMenu = (
    event: React.MouseEvent<HTMLDivElement>,
    container: PlanogramContainer,
  ) => {
    const options: MenuOption[] = [
      {
        label: "Split to Right",
        keybind: {
          ctrl: true,
          shift: true,
          keyCode: KeyCode.X,
        },
        onClick: () => splitContainerRight(container),
      },
      {
        label: "Split to Bottom",
        keybind: {
          ctrl: true,
          shift: true,
          keyCode: KeyCode.Y,
        },
        onClick: () => splitContainerBottom(container),
      },
      {
        label: "Unrange",
        keybind: {
          keyCode: KeyCode.U,
        },
        onClick: () => unrangeContainer(container),
      },
      {
        label: "Delete",
        keybind: {
          keyCode: KeyCode.DELETE,
        },
        onClick: () => deleteContainer(container),
      },
    ];

    openMenu({
      event,
      menu: {
        title: container.getLabel(),
        options,
      },
    });
  };

  const getContainers = () => {
    const splitContainers: {
      [label: string]: { totalNo: number; currentNo: number; algoTargetWidthSum: number };
    } = {};

    for (const container of pog.containers) {
      const splitContainer = splitContainers[container.getLabel()];
      if (splitContainer) {
        splitContainer.totalNo++;
        splitContainer.algoTargetWidthSum += container.algoCurrentTargetWidth || 0;
      } else {
        splitContainers[container.getLabel()] = {
          totalNo: 1,
          currentNo: 1,
          algoTargetWidthSum: container.algoCurrentTargetWidth || 0,
        };
      }
    }

    return pog.containers.map((container, containerIndex) => {
      const splitContainer = splitContainers[container.getLabel()];
      const splitContainerNo = splitContainer.currentNo++;

      const isInvalid =
        container.algoMinTargetWidth != undefined &&
        splitContainer.algoTargetWidthSum < container.algoMinTargetWidth;

      let label = container.getLabel();

      if ("cdt2" in container.cdts[0]) {
        label = container.cdts[0].cdt2;
      }

      if (splitContainer.totalNo > 1) {
        label += ` ${splitContainerNo}`;
      }

      const { minProductHeightMet, maxProductHeightMet } = calculateContainerAlgoPoints(
        container,
        pog,
      );

      return (
        <Container
          key={containerIndex}
          container={{
            ...rectToScene(container),
            label,
            algoCurrentTargetWidth: splitContainer.algoTargetWidthSum,
          }}
          scaleX={scaleX}
          scaleY={scaleY}
          bounds={bounds && { ...bounds, xRight: toSceneX(bounds.xRight) }}
          snappables={getSnappables(container)}
          isDraggable={isEditable}
          isResizeable={isEditable}
          isInvalid={isInvalid}
          isHoverSensitive={isEditable || !isContainersFullDetails}
          isMinProductHeightMet={minProductHeightMet}
          isMaxProductHeightMet={maxProductHeightMet}
          onModify={(modifications) => {
            onModify(container, modifications);
          }}
          onEnter={() => setHoveredContainer(container)}
          onLeave={() => setHoveredContainer(null)}
          onContext={(event) => onContainerMenu(event, container)}
        />
      );
    });
  };

  return (
    <InnerWrapper className="containers" $zIndex={PLANOGRAM_LAYER_Z_INDEX.containers}>
      {getContainers()}
    </InnerWrapper>
  );
};
